import { MemberMap } from '@nextforce/api'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { useMembers } from 'chat/members/queries/list'
import { setReadByCount } from 'chat/messages/queries/shared/utils'
import keys from 'chat/shared/queries/keys'
import { convertReadReceipt } from './shared/convert'
import * as Response from './shared/response'
import { ReadersData } from './shared/types'

export function useReaders(conversationId: string, messageId: string) {
  const { server } = useServer()
  const client = useQueryClient()
  const { data: members, isLoading: isMembersLoading } =
    useMembers(conversationId)

  return useQuery<ReadersData>({
    queryKey: keys.readers(conversationId, messageId),
    queryFn: fetchReaders,
    refetchInterval: 3000,
    enabled: !!members && !isMembersLoading, // Only enable the query when members are available
  })

  async function fetchReaders() {
    if (!members) {
      return []
    }

    const memberMap = members.reduce<MemberMap>(
      (map, member) => ({ ...map, [member.id]: member }),
      {}
    )

    const response = await server.get<Response.ReadReceipt[]>(
      `/v2/chat/conversations/${conversationId}/messages/${messageId}/read_receipts`
    )
    const convertedResponse = response.flatMap(
      readReceipt => convertReadReceipt(readReceipt, memberMap) ?? []
    )
    setReadByCount(
      client,
      conversationId,
      messageId,
      convertedResponse.length
    ).catch(e => null)
    return convertedResponse
  }
}
